import { useState, useEffect, useMemo } from 'react';
import Sidebar from '../../components/feature/Sidebar';
import { api } from '../../services/api';
import { formatPrice } from '../../utils/format';
import { Toast, useToast } from '../../components/base/Toast';
import OfflineIndicator from '../../components/base/OfflineIndicator';

interface Transaction {
  id: number;
  type: 'ENTREE' | 'SORTIE';
  categorie: string;
  montant: number;
  description: string;
  date_transaction: string;
  vendeur_nom: string;
  reference_type: string;
  reference_id: number;
  boutique_id?: number;
}

export default function FinancesPage() {
  const { toast, showToast, hideToast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [filterType, setFilterType] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [activeBoutique, setActiveBoutique] = useState<number | 'toutes'>('toutes');

  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const filteredTransactions = useMemo(() => {
    return transactions.filter(t => {
      const matchesSearch = !searchQuery || 
        (t.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.categorie || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.vendeur_nom || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(t.montant).includes(searchQuery);

      const tDate = (t.date_transaction || '').split(' ')[0];
      const matchesStart = !startDate || tDate >= startDate;
      const matchesEnd = !endDate || tDate <= endDate;

      return matchesSearch && matchesStart && matchesEnd;
    });
  }, [transactions, searchQuery, startDate, endDate]);

  const dynamicStats = useMemo(() => {
    let entrees = 0;
    let sorties = 0;
    filteredTransactions.forEach(t => {
      if (t.type === 'ENTREE') {
        entrees += Number(t.montant);
      } else {
        sorties += Number(t.montant);
      }
    });
    return { entrees, sorties, solde: entrees - sorties };
  }, [filteredTransactions]);

  const dynamicB1Solde = useMemo(() => {
    return filteredTransactions
      .filter(t => Number(t.boutique_id) === 1)
      .reduce((sum, t) => sum + (t.type === 'ENTREE' ? Number(t.montant) : -Number(t.montant)), 0);
  }, [filteredTransactions]);

  const dynamicB2Solde = useMemo(() => {
    return filteredTransactions
      .filter(t => Number(t.boutique_id) === 2)
      .reduce((sum, t) => sum + (t.type === 'ENTREE' ? Number(t.montant) : -Number(t.montant)), 0);
  }, [filteredTransactions]);

  // Form state
  const [newTrans, setNewTrans] = useState({
    type: 'SORTIE',
    categorie: '',
    montant: '',
    description: '',
    date_transaction: new Date().toISOString().split('T')[0],
    boutique_id: 1
  });

  useEffect(() => {
    const stored = localStorage.getItem('atelier_bassoum_user');
    if (stored) {
      const parsed = JSON.parse(stored);
      setUser(parsed);
      if (parsed.role === 'vendeur') {
        setActiveBoutique(parsed.boutique_id || 1);
        setNewTrans(prev => ({ ...prev, boutique_id: parsed.boutique_id || 1 }));
      }
    }
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token || !user) return;
    setLoading(true);
    try {
      const boutiqueParam = activeBoutique === 'toutes' ? undefined : Number(activeBoutique);
      const [transData, statsData, catsData] = await Promise.all([
        api.getFinances(token, { type: filterType, boutique_id: boutiqueParam }),
        api.getFinanceStats(token, boutiqueParam),
        api.getFinanceCategories(token)
      ]);
      setTransactions(Array.isArray(transData) ? transData : []);
      setStats(statsData || {});
      setCategories(Array.isArray(catsData) ? catsData : []);
    } catch (e) {
      showToast('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [filterType, activeBoutique, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    if (!newTrans.categorie || !newTrans.montant) {
      showToast('Veuillez remplir les champs obligatoires.', 'error');
      return;
    }

    try {
      await api.addFinance(token, {
        ...newTrans,
        montant: Number(newTrans.montant)
      });
      showToast('Transaction enregistrée !', 'success');
      setShowModal(false);
      setNewTrans({
        type: 'SORTIE',
        categorie: '',
        montant: '',
        description: '',
        date_transaction: new Date().toISOString().split('T')[0],
        boutique_id: user?.boutique_id || 1
      });
      fetchData();
    } catch (e) {
      showToast('Erreur lors de l\'enregistrement', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer cette transaction ?')) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.deleteFinance(token, id);
      showToast('Supprimé.', 'success');
      fetchData();
    } catch (e) {
      showToast('Erreur lors de la suppression', 'error');
    }
  };

  if (!user) return null;

  return (
    <div className="flex h-screen bg-[#F8FAFC] font-nunito overflow-hidden relative">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      
      {/* Mobile Overlay */}
      {!sidebarCollapsed && (
        <div 
          className="fixed inset-0 bg-black/50 z-[105] md:hidden animate-fade-in"
          onClick={() => setSidebarCollapsed(true)}
        />
      )}
      
      <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b border-gray-100 px-4 md:px-8 py-4 md:py-5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setSidebarCollapsed(false)}
              title="Ouvrir le menu"
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl bg-gray-50 text-gray-600 cursor-pointer"
            >
              <i className="ri-menu-2-line text-xl" />
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-black text-gray-900 font-playfair tracking-tight">Trésorerie & Journal</h1>
              <p className="text-gray-400 text-[10px] md:text-xs font-bold uppercase tracking-widest mt-0.5">Suivi des flux de caisse</p>
            </div>
          </div>
          <button 
            onClick={() => setShowModal(true)}
            className="bg-foret-500 hover:bg-foret-600 text-white p-2.5 md:px-5 md:py-2.5 rounded-2xl flex items-center gap-2 shadow-lg shadow-foret-500/20 transition-all active:scale-95 font-black text-sm cursor-pointer"
          >
            <i className="ri-add-line text-lg" />
            <span className="hidden md:inline">NOUVELLE DÉPENSE</span>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-8 lg:p-10">
          <div className="max-w-6xl mx-auto space-y-8">
            {user.role !== 'vendeur' && (
              <div className="flex gap-2 bg-white border border-gray-150 rounded-2xl p-1 shadow-sm max-w-xs animate-fade-in">
                <button
                  type="button"
                  onClick={() => setActiveBoutique('toutes')}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${activeBoutique === 'toutes' ? 'bg-foret-500 text-white shadow-md shadow-foret-100' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                  Toutes
                </button>
                <button
                  type="button"
                  onClick={() => setActiveBoutique(1)}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${activeBoutique === 1 ? 'bg-foret-500 text-white shadow-md shadow-foret-100' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                  Boutique 1
                </button>
                <button
                  type="button"
                  onClick={() => setActiveBoutique(2)}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${activeBoutique === 2 ? 'bg-foret-500 text-white shadow-md shadow-foret-100' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                  Boutique 2
                </button>
              </div>
            )}
            
            {/* Statistiques Rapides */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-5 rounded-3xl border border-gray-100 shadow-sm transition-all hover:shadow-md">
                <div className="w-10 h-10 bg-green-50 text-green-600 rounded-xl flex items-center justify-center mb-3">
                  <i className="ri-arrow-left-down-line text-xl" />
                </div>
                <p className="text-gray-400 text-[10px] uppercase font-black tracking-wider">
                  {searchQuery || startDate || endDate ? 'Entrées filtrées' : 'Entrées (Période)'}
                </p>
                <p className="text-2xl font-black text-green-600 mt-1">
                  {formatPrice(dynamicStats.entrees)}
                </p>
              </div>

              <div className="bg-white p-5 rounded-3xl border border-gray-100 shadow-sm transition-all hover:shadow-md">
                <div className="w-10 h-10 bg-red-50 text-red-600 rounded-xl flex items-center justify-center mb-3">
                  <i className="ri-arrow-right-up-line text-xl" />
                </div>
                <p className="text-gray-400 text-[10px] uppercase font-black tracking-wider">
                  {searchQuery || startDate || endDate ? 'Sorties filtrées' : 'Sorties (Période)'}
                </p>
                <p className="text-2xl font-black text-red-500 mt-1">
                  {formatPrice(dynamicStats.sorties)}
                </p>
              </div>

              <div className="bg-gradient-to-br from-foret-600 to-foret-800 p-6 rounded-3xl shadow-xl shadow-foret-600/20 md:col-span-2 relative overflow-hidden group">
                <div className="relative z-10">
                  <p className="text-foret-100 text-[11px] uppercase font-black tracking-[0.2em] mb-1">
                    {searchQuery || startDate || endDate ? 'Solde de la sélection' : 'Solde actuel en caisse'}
                  </p>
                  <p className="text-4xl font-black text-white">
                    {formatPrice(searchQuery || startDate || endDate ? dynamicStats.solde : (stats?.solde || 0))}
                  </p>
                  
                  <div className="mt-4 grid grid-cols-2 gap-4 pt-3 border-t border-white/10">
                    <div>
                      <p className="text-foret-200 text-[10px] uppercase font-bold">Boutique 1</p>
                      <p className="text-sm font-bold text-white">
                        {formatPrice(searchQuery || startDate || endDate ? dynamicB1Solde : (stats?.solde_b1 || 0))}
                      </p>
                    </div>
                    <div>
                      <p className="text-foret-200 text-[10px] uppercase font-bold">Boutique 2</p>
                      <p className="text-sm font-bold text-white">
                        {formatPrice(searchQuery || startDate || endDate ? dynamicB2Solde : (stats?.solde_b2 || 0))}
                      </p>
                    </div>
                  </div>
                </div>
                <i className="ri-bank-card-line absolute top-[-10px] right-[-10px] text-white/5 text-9xl group-hover:scale-110 transition-transform duration-700" />
              </div>
            </div>

            {/* Liste des Transactions */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden min-h-[500px]">
              <div className="px-6 py-5 border-b border-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gray-50/50">
                <h3 className="font-playfair font-black text-lg text-gray-800">Journal d'activité financière</h3>
                
                <div className="flex gap-2">
                  <button 
                    onClick={() => setFilterType('')}
                    className={`px-4 py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer ${filterType === '' ? 'bg-gray-800 text-white shadow-md' : 'text-gray-500 hover:bg-gray-100'}`}
                  >
                    TOUT
                  </button>
                  <button 
                    onClick={() => setFilterType('ENTREE')}
                    className={`px-4 py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer ${filterType === 'ENTREE' ? 'bg-green-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-100'}`}
                  >
                    ENTRÉES
                  </button>
                  <button 
                    onClick={() => setFilterType('SORTIE')}
                    className={`px-4 py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer ${filterType === 'SORTIE' ? 'bg-red-500 text-white shadow-md' : 'text-gray-500 hover:bg-gray-100'}`}
                  >
                    SORTIES
                  </button>
                </div>
              </div>

              {/* Barre de recherche et de filtrage par date */}
              <div className="px-6 py-4 border-b border-gray-100 bg-white flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="relative w-full md:max-w-xs">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold" />
                  <input 
                    type="text" 
                    placeholder="Rechercher..." 
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-8 py-2 rounded-xl border border-gray-200 bg-gray-50 text-xs font-bold text-gray-800 placeholder-gray-400 focus:bg-white focus:border-foret-500 focus:ring-0 transition-all outline-none"
                  />
                  {searchQuery && (
                    <button 
                      type="button" 
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Effacer"
                    >
                      <i className="ri-close-circle-fill text-base" />
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5">
                    <span className="text-[10px] font-black text-gray-400 uppercase">Du</span>
                    <input 
                      type="date" 
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      className="bg-transparent text-xs font-bold text-gray-700 outline-none border-none p-0 cursor-pointer"
                    />
                  </div>

                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5">
                    <span className="text-[10px] font-black text-gray-400 uppercase">Au</span>
                    <input 
                      type="date" 
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      className="bg-transparent text-xs font-bold text-gray-700 outline-none border-none p-0 cursor-pointer"
                    />
                  </div>

                  {(searchQuery || startDate || endDate) && (
                    <button 
                      onClick={() => { setSearchQuery(''); setStartDate(''); setEndDate(''); }}
                      className="px-3 py-1.5 text-xs font-bold text-red-500 hover:bg-red-50 rounded-xl transition-all cursor-pointer flex items-center gap-1"
                    >
                      <i className="ri-restart-line" />
                      Réinitialiser
                    </button>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto hidden md:block">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-50">
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Date</th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Catégorie / Type</th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Description</th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Montant</th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {loading ? (
                      [1,2,3,4,5].map(v => (
                        <tr key={v} className="animate-pulse">
                          <td colSpan={5} className="px-6 py-4"><div className="h-10 bg-gray-50 rounded-xl" /></td>
                        </tr>
                      ))
                    ) : filteredTransactions.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-20 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <i className="ri-inbox-line text-4xl text-gray-200" />
                            <p className="text-gray-400 font-bold">Aucune transaction trouvée</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredTransactions.map(t => (
                        <tr key={t.id} className="hover:bg-gray-50/50 transition-colors group">
                          <td className="px-6 py-4">
                            <span className="text-xs font-black text-gray-800">{new Date(t.date_transaction).toLocaleDateString('fr-FR', { day:'2-digit', month:'long' })}</span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-black text-gray-900">{t.categorie}</span>
                                <span className="text-[9px] bg-blue-50 text-blue-600 font-bold px-1.5 py-0.2 rounded flex-shrink-0">B{t.boutique_id || 1}</span>
                                {t.description?.toLowerCase().includes('acompte') && (
                                  <span className="text-[8px] bg-sky-50 text-sky-700 font-black px-1.5 py-0.5 rounded border border-sky-200 uppercase tracking-wider scale-90 origin-left">Acompte</span>
                                )}
                                {(t.description?.toLowerCase().includes('dette') || t.description?.toLowerCase().includes('versement')) && (
                                  <span className="text-[8px] bg-green-50 text-green-700 font-black px-1.5 py-0.5 rounded border border-green-200 uppercase tracking-wider scale-90 origin-left">Dette payée</span>
                                )}
                              </div>
                              <span className={`text-[9px] font-black uppercase tracking-widest ${t.type === 'ENTREE' ? 'text-green-600' : 'text-red-500'}`}>
                                {t.type}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className="text-xs text-gray-600 line-clamp-1">{t.description || '—'}</span>
                              <span className="text-[10px] text-gray-400">Encaissé par {t.vendeur_nom}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <span className={`text-sm font-black ${t.type === 'ENTREE' ? 'text-green-600' : 'text-red-500'}`}>
                              {t.type === 'ENTREE' ? '+' : '-'} {formatPrice(t.montant, false)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                             {t.reference_type === 'MANUEL' && (
                               <button 
                                onClick={() => handleDelete(t.id)}
                                title="Supprimer cette transaction"
                                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100"
                               >
                                 <i className="ri-delete-bin-line" />
                               </button>
                             )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile View: Cards */}
              <div className="md:hidden divide-y divide-gray-50">
                {loading ? (
                  [1,2,3].map(v => (
                    <div key={v} className="p-4 animate-pulse"><div className="h-20 bg-gray-50 rounded-2xl" /></div>
                  ))
                ) : filteredTransactions.length === 0 ? (
                  <div className="p-10 text-center text-gray-400 text-sm font-bold">Aucune transaction</div>
                ) : (
                  filteredTransactions.map(t => (
                    <div key={t.id} className="p-4 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${t.type === 'ENTREE' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                          <i className={t.type === 'ENTREE' ? 'ri-arrow-left-down-line' : 'ri-arrow-right-up-line'} />
                        </div>
                        <div>
                          <p className="text-xs font-black text-gray-900 line-clamp-1">{t.description || t.categorie}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-gray-400 uppercase font-bold">{new Date(t.date_transaction).toLocaleDateString('fr-FR', { day:'2-digit', month:'short' })}</span>
                            <span className="text-[10px] text-gray-300">•</span>
                            <span className="text-[10px] text-gray-400 font-bold">{t.categorie} (B{t.boutique_id || 1})</span>
                            {t.description?.toLowerCase().includes('acompte') && (
                              <span className="text-[8px] bg-sky-50 text-sky-700 font-black px-1 rounded border border-sky-100 uppercase tracking-wider scale-90 origin-left">Acompte</span>
                            )}
                            {(t.description?.toLowerCase().includes('dette') || t.description?.toLowerCase().includes('versement')) && (
                              <span className="text-[8px] bg-green-50 text-green-700 font-black px-1 rounded border border-green-100 uppercase tracking-wider scale-90 origin-left">Dette payée</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-black ${t.type === 'ENTREE' ? 'text-green-600' : 'text-red-500'}`}>
                          {t.type === 'ENTREE' ? '+' : '-'} {formatPrice(t.montant, false)}
                        </p>
                        {t.reference_type === 'MANUEL' && (
                          <button onClick={() => handleDelete(t.id)} className="text-[10px] text-red-400 font-bold mt-1">Supprimer</button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modal Nouvelle Transaction */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm animate-fade-in" onClick={() => setShowModal(false)} />
          <div className="bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl relative z-10 animate-scale-in max-h-[90vh] flex flex-col overflow-hidden border border-gray-100">
            <header className="px-8 py-6 bg-gray-50 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-xl font-black text-gray-900 font-playfair">Nouvelle opération</h2>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Enregistrement manuel</p>
              </div>
              <button 
                onClick={() => setShowModal(false)}
                title="Fermer"
                className="w-10 h-10 rounded-2xl flex items-center justify-center text-gray-400 hover:bg-white hover:text-gray-900 transition-all shadow-sm cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </header>

            <form onSubmit={handleSubmit} className="p-8 space-y-5 overflow-y-auto">
              <div className="flex bg-gray-100 p-1 rounded-2xl gap-1">
                <button
                  type="button"
                  onClick={() => setNewTrans({...newTrans, type: 'ENTREE'})}
                  className={`flex-1 py-3 rounded-xl text-xs font-black transition-all cursor-pointer ${newTrans.type === 'ENTREE' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-400'}`}
                >
                  <i className="ri-arrow-left-down-line mr-2" /> ENTRÉE
                </button>
                <button
                  type="button"
                  onClick={() => setNewTrans({...newTrans, type: 'SORTIE'})}
                  className={`flex-1 py-3 rounded-xl text-xs font-black transition-all cursor-pointer ${newTrans.type === 'SORTIE' ? 'bg-white text-red-500 shadow-sm' : 'text-gray-400'}`}
                >
                  <i className="ri-arrow-right-up-line mr-2" /> DÉPENSE (SORTIE)
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Montant (FCFA)</label>
                  <div className="relative">
                    <i className="ri-money-franc-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input 
                      type="number"
                      required
                      value={newTrans.montant}
                      onChange={e => setNewTrans({...newTrans, montant: e.target.value})}
                      placeholder="0"
                      className="w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-100 bg-gray-50 font-black text-gray-900 focus:bg-white focus:border-foret-500 focus:ring-0 transition-all outline-none"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Date</label>
                  <div className="relative">
                    <i className="ri-calendar-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input 
                      type="date"
                      required
                      title="Choisir une date"
                      value={newTrans.date_transaction}
                      onChange={e => setNewTrans({...newTrans, date_transaction: e.target.value})}
                      className="w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-100 bg-gray-50 font-black text-gray-900 focus:bg-white focus:border-foret-500 focus:ring-0 transition-all outline-none"
                    />
                  </div>
                </div>
              </div>

              {user.role !== 'vendeur' && (
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Caisse / Boutique *</label>
                  <div className="relative">
                    <i className="ri-store-2-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold" />
                    <select
                      value={newTrans.boutique_id}
                      onChange={e => setNewTrans({...newTrans, boutique_id: Number(e.target.value)})}
                      className="w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-100 bg-gray-50 font-bold text-gray-900 focus:bg-white focus:border-foret-500 focus:ring-0 transition-all outline-none appearance-none cursor-pointer"
                      title="Sélectionner la boutique pour cette opération"
                    >
                      <option value={1}>Boutique 1</option>
                      <option value={2}>Boutique 2</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="space-y-1.5 relative">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Catégorie</label>
                <div className="relative">
                  <i className="ri-price-tag-3-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input 
                    type="text"
                    required
                    list="categories-list"
                    value={newTrans.categorie}
                    onChange={e => setNewTrans({...newTrans, categorie: e.target.value})}
                    placeholder="Saisir ou choisir..."
                    className="w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-100 bg-gray-50 font-bold text-gray-900 focus:bg-white focus:border-foret-500 focus:ring-0 transition-all outline-none"
                  />
                  <datalist id="categories-list">
                    {categories.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Description (Optionnel)</label>
                <textarea 
                  value={newTrans.description}
                  onChange={e => setNewTrans({...newTrans, description: e.target.value})}
                  rows={3}
                  placeholder="Plus de détails sur cette opération..."
                  className="w-full p-4 rounded-2xl border border-gray-100 bg-gray-50 font-medium text-gray-800 focus:bg-white focus:border-foret-500 focus:ring-0 transition-all outline-none"
                />
              </div>

              <button 
                type="submit"
                className="w-full bg-foret-500 hover:bg-foret-600 text-white py-4 rounded-2xl font-black text-sm shadow-xl shadow-foret-500/20 transition-all active:scale-95 mt-4 cursor-pointer"
              >
                VALIDER L'OPÉRATION
              </button>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scale-in { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
        .animate-scale-in { animation: scale-in 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
      `}</style>
    </div>
  );
}
